<script setup>
import { timeToRead } from '~/utils/timeToRead'
import { DateTime } from 'luxon'

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  slug: {
    type: String,
    required: true,
  },
  category: {
    type: String,
    default: null,
  },
  image: {
    type: Object,
    default: null,
  },
  date: {
    type: String,
    default: null,
  },
  highlight: {
    type: Boolean,
    default: false,
  },
  copy: {
    type: Object,
    default: null,
  },
})

const formattedDate = computed(() =>
  DateTime.fromISO(props.date).toLocaleString(DateTime.DATE_FULL)
)

const timeDisplay = computed(() => props.copy && timeToRead(props.copy))
const categoryDisplay = computed(() => props.category || 'News')
</script>

<template>
  <AnimatedCard
    class="news-article"
    :class="{ 'news-article--large': highlight }"
  >
    <NuxtLink :to="`/news/${slug}`" class="news-article__link gtm-vgms-news-single-post" :data-article-title='title'>
      <div class="news-article__header text__eyebrow-medium">
        <span class="news-article__header-item--category">{{
          categoryDisplay
        }}</span>
        <span v-if="timeDisplay" class="news-article__header-item--time"
          > • {{ timeDisplay }}</span
        >
        <span class="news-article__header-circles" />
      </div>

      <figure v-if="image" class="news-article__image">
        <img :src="image.thumbnail" alt=""/>
      </figure>

      <div class="news-article__content">
        <h3 class="news-article__content-title text__copy-medium">
          {{ title }}
        </h3>

        <p class="news-article__content-date text__eyebrow-medium">
          {{ formattedDate }}
        </p>
      </div>
    </NuxtLink>
  </AnimatedCard>
</template>

<style lang="scss">
// Vendors
@import 'styles/vendors/sass-mq/mq';

@import '~/styles/utils/functions';
@import '~/styles/utils/timing-functions';

.news-article {
  position: relative;
  background-color: #1c1229;
  aspect-ratio: 314/460;
  display: flex;
  flex-direction: column;

  &__link {
    display: flex;
    flex-direction: column;
    text-align: left;
    color: $brand-white;
    width: 100%;
    height: 100%;

    &:hover,
    &:focus {
      .news-article__image img {
        transform: scale(1.1);
      }
    }
  }

  &__header {
    position: relative;
    height: 60px;
    padding-left: 26px;
    padding-right: 26px;
    display: flex;
    align-items: center;
    width: 100%;
    flex-shrink: 0;
    overflow: hidden;

    &-circles {
      pointer-events: none;

      &::before,
      &::after {
        position: absolute;
        top: 45%;
        left: 35px;
        transform: translate(-50%, -50%);
        content: '';
        border-radius: 50%;
        border: 1px solid #c80afb;
      }

      &::before {
        width: 91px;
        height: 91px;
        opacity: 0.1;
      }

      &::after {
        width: 107px;
        height: 107px;
        opacity: 0.05;
      }
    }

    &::before {
      position: absolute;
      top: 0;
      left: 40px;
      width: 450px;
      height: 331px;
      background: radial-gradient(
        50% 50% at 50% 50%,
        #c80afb 0%,
        rgba(200, 10, 251, 0.63) 31.77%,
        rgba(200, 10, 251, 0.36) 60.94%,
        rgba(200, 10, 251, 0) 99.94%
      );
      transform: translate(-50%, -50%);
      opacity: 0.2;
      pointer-events: none;
      content: '';
    }

    &-item {
      position: relative;
      z-index: 2;
      text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.12);

      &--category {
        color: #c80afb;
      }

      &--time {
        color: rgba($brand-white, 0.7);
      }
    }
  }

  &__image {
    position: relative;
    display: block;
    aspect-ratio: 652/434;
    flex-shrink: 0;
    overflow: hidden;

    img {
      position: absolute;
      top: 0;
      left: 0;
      object-fit: cover;
      display: block;
      width: 100%;
      height: 100%;
      transition: transform 0.3s $ease-in-out-quart;
    }
  }

  &__content {
    padding: 26px;
    display: flex;
    flex-direction: column;
    height: 100%;

    &-title {
      text-align: left;
      color: $brand-white;
      overflow: hidden;
      display: -webkit-box;
      text-overflow: ellipsis;
      -webkit-line-clamp: 3;
      max-height: 78px;
      -webkit-box-orient: vertical;

      @include mq($from: 'ml') using ($from) {
        max-height: 135px;
      }
    }

    &-date {
      margin-top: auto;
      color: rgba(#a9a7b2, 0.6);
      font-size: 10px;
      letter-spacing: 0.18em;

      @include mq($from: 'ml') using ($from) {
        color: rgba($brand-white, 0.7);
        font-size: 11px;
        letter-spacing: 0.24em;
      }
    }
  }

  &--large {
    @include mq($from: 'ml') using ($from) {
      aspect-ratio: 652/730;

      .news-article__content {
        padding: 34px 26px 26px;
      }
    }

    .news-article__content-title {
      @include mq($from: 'ml') using ($from) {
        font-size: 24px;
      }
    }
  }
}
</style>
